import React, { useState, useEffect } from 'react';
import { Heart, Users, MessageCircle, Sparkles, Layout, Palette, Zap, CheckCircle, ArrowRight, Github, Figma } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Header/Header';
import Footer from '../../Footer/Footer';
import styles from './DatingappFigma.module.css';

export default function DatingAppCaseStudy() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const [mode, setMode] = useState('web');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      setScrolled(window.scrollY > 50);
      const sections = document.querySelectorAll('.section');
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 200 && rect.bottom >= 200) {
          setActiveSection(index);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: Users, title: 'Easy Onboarding', desc: 'Quick signup process to get users started' },
    { icon: Heart, title: 'Smart Matching', desc: 'Algorithm-based compatibility matching' },
    { icon: MessageCircle, title: 'Seamless Chat', desc: 'Real-time messaging with read receipts' },
    { icon: Sparkles, title: 'Fun Interactions', desc: 'Engaging swipe gestures and animations' }
  ];

  const challenges = [
    {
      challenge: 'Designing an intuitive discover screen without clutter',
      solution: 'Used large profile cards, minimal text, and consistent gestures'
    },
    {
      challenge: 'Keeping chat readable and modern',
      solution: 'Clear typography, bubbles, and timestamps'
    },
    {
      challenge: 'Balancing features with simplicity',
      solution: 'Progressive disclosure and prioritized core actions'
    }
  ];

  const screens = [
    { title: 'Onboarding', desc: 'Attractive welcome screens with short signup process' },
    { title: 'Discover', desc: 'Clean swipe interface with profile cards' },
    { title: 'Match & Chat', desc: 'Easy access to matches with clear chat UI' },
    { title: 'Profile', desc: 'Simple edit flow with safety features' }
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background */}
      <div className={styles.backgroundFixed}>
        <div className={styles.blobPink1} />
        <div className={styles.blobPurple} />
        <div className={styles.blobPink2} />
      </div>

      {/* Hero Section */}
      <div className={styles.section}>
        <div className={`${styles.maxW6xl} ${styles.textCenter} ${styles.relativeZ10}`}>
          <div className={styles.badge}>
            📱 Case Study
          </div>
          <h1 className={styles.heroTitle}>
            Dating App
          </h1>
          <p className={styles.heroSubtitle}>
            Creating meaningful connections through intuitive design
          </p>
          <div className={styles.tagsContainer}>
            <span className={styles.tag}>
              🛠 Figma
            </span>
            <span className={styles.tag}>
              📆 1 Month
            </span>
            <span className={styles.tag}>
              👤 UX/UI Designer
            </span>
          </div>
          
          <div className={styles.buttonContainer}>
            <button className={styles.primaryButton}>
              <Figma className="w-5 h-5" />
              View Prototype
              <ArrowRight className={`w-5 h-5 ${styles.arrowIcon}`} />
            </button>
          </div>
        </div>

        {/* Floating Hearts Animation */}
        <div className={styles.floatingHearts}>
          {[...Array(8)].map((_, i) => (
            <Heart
              key={i}
              className={styles.floatingHeart}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${15 + Math.random() * 10}s`
              }}
              size={30 + Math.random() * 20}
            />
          ))}
        </div>
      </div>

      {/* Problem Statement */}
      <div className={styles.sectionPy20}>
        <div className={styles.maxW5xl}>
          <div className={styles.card}>
            <div className={`flex items-center gap-4 ${styles.mb6}`}>
              <h2 className={styles.sectionTitle}>Problem Statement</h2>
            </div>
            <p className={styles.textXl}>
              Users struggle with <span className={`${styles.fontSemibold} ${styles.textPink600}`}>confusing navigation</span> and 
              lack of <span className={`${styles.fontSemibold} ${styles.textPurple600}`}>meaningful interaction features</span> in 
              existing dating apps. The goal was to design a clean, engaging experience that makes discovering 
              and chatting with matches <span className={styles.fontSemibold}>easy and delightful</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Target Users */}
      <div className={styles.sectionBg}>
        <div className={styles.maxW6xl}>
          <h2 className={styles.sectionTitle}>
            Target Users & Research
          </h2>
          <div className={styles.grid3}>
            <div className={styles.featureCard}>
              <div className={styles.userIcon1}>
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className={styles.cardTitle}>Demographics</h3>
              <p className={styles.textGray700}>Singles aged <strong>20-35</strong>, active mobile users seeking meaningful relationships and connections</p>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.userIcon2}>
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className={styles.cardTitle}>User Needs</h3>
              <ul className={`${styles.textGray700} ${styles.spaceY2}`}>
                <li>Easy onboarding</li>
                <li>Quick discovery of matches</li>
                <li>Secure communication</li>
                <li>Clear profile browsing</li>
              </ul>
            </div>
            <div className={styles.featureCard}>
              <div className={styles.userIcon3}>
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className={styles.cardTitle}>Pain Points</h3>
              <p className={styles.textGray700}>Confusing navigation, lack of meaningful features, cluttered interfaces, and poor communication tools</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className={styles.sectionPy20}>
        <div className={styles.maxW6xl}>
          <h2 className={styles.sectionTitle}>
            Key Features
          </h2>
          <div className={styles.grid4}>
            {features.map((feature, index) => (
              <div
                key={index}
                className={styles.featureCard}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={styles.featureIcon}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.textGray600}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Design Screens */}
      <div className={styles.sectionBg}>
        <div className={styles.maxW6xl}>
          <h2 className={styles.sectionTitle}>
            High-Fidelity Screens
          </h2>
          <div className={styles.grid4}>
            {screens.map((screen, index) => (
              <div
                key={index}
                className={styles.screenCard}
              >
                <div className={`${styles.screenMockup} ${styles[`screenPreview${index + 1}`]}`}>
                </div>
                <div className={styles.screenContent}>
                  <h3 className={styles.cardTitle}>{screen.title}</h3>
                  <p className={styles.textGray600}>{screen.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Challenges & Solutions */}
      <div className={styles.sectionPy20}>
        <div className={styles.maxW5xl}>
          <h2 className={styles.sectionTitle}>
            Challenges & Solutions
          </h2>
          <div className={styles.spaceY8}>
            {challenges.map((item, index) => (
              <div
                key={index}
                className={styles.challengeCard}
              >
                <div className={styles.gridChallenges}>
                  <div>
                    <h3 className={`${styles.textLg} ${styles.fontBold} ${styles.textPink600} ${styles.mb3} ${styles.uppercase} ${styles.trackingWider}`}>Challenge</h3>
                    <p className={`${styles.textGray700} ${styles.textLg}`}>{item.challenge}</p>
                  </div>
                  <div>
                    <h3 className={`${styles.textLg} ${styles.fontBold} ${styles.textPurple600} ${styles.mb3} ${styles.uppercase} ${styles.trackingWider}`}>Solution</h3>
                    <p className={`${styles.textGray700} ${styles.textLg}`}>{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Outcome */}
      <div className={styles.sectionBg}>
        <div className={styles.maxW6xl}>
          <h2 className={styles.sectionTitle}>
            Outcome & Learnings
          </h2>
          <div className={styles.grid2}>
            <div className={styles.outcomeCard}>
              <div className={`flex items-center gap-4 ${styles.mb6}`}>
                <CheckCircle className={`${styles.outcomeIcon} ${styles.textGreen500}`} />
                <h3 className={`text-3xl ${styles.fontBold} ${styles.textGray700}`}>Outcome</h3>
              </div>
              <ul className={`${styles.spaceY4} ${styles.textGray700} ${styles.textLg}`}>
                <li className={styles.listItem}>
                  <span className={`${styles.textPink500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>Completed all major screens with consistent UI</span>
                </li>
                <li className={styles.listItem}>
                  <span className={`${styles.textPurple500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>Interactive prototype created in Figma</span>
                </li>
                <li className={styles.listItem}>
                  <span className={`${styles.textPink500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>Usability improved based on feedback</span>
                </li>
              </ul>
            </div>
            <div className={styles.outcomeCard}>
              <div className={`flex items-center gap-4 ${styles.mb6}`}>
                <Sparkles className={`${styles.outcomeIcon} ${styles.textPurple500}`} />
                <h3 className={`text-3xl ${styles.fontBold} ${styles.textGray700}`}>What I Learned</h3>
              </div>
              <ul className={`${styles.spaceY4} ${styles.textGray700} ${styles.textLg}`}>
                <li className={styles.listItem}>
                  <span className={`${styles.textPurple500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>Better use of Figma components and auto layout</span>
                </li>
                <li className={styles.listItem}>
                  <span className={`${styles.textPink500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>Importance of micro-interactions for engagement</span>
                </li>
                <li className={styles.listItem}>
                  <span className={`${styles.textPurple500} ${styles.fontBold} ${styles.listBullet}`}>•</span>
                  <span>How to think from both UX and UI perspectives</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* View Full Design in Figma CTA */}
      <div className={styles.ctaSection}>
        <div className={styles.ctaCard}>
          <h2 className={styles.ctaTitle}>
            View Full Design in Figma
          </h2>
          <p className={styles.ctaText}>
            Explore interactive prototypes, detailed wireframes, and complete design system
          </p>
          <a
            href="https://www.figma.com/proto/VTvwvBGVKbTYPQcH2GX5So/Dating-APP?page-id=0%3A1&node-id=48-41&starting-point-node-id=48%3A41&t=qrbPQSJVlfoDOC2B-1"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaLinkButton}
          >
            Open Figma Project →
          </a>
        </div>
      </div>

      {/* Get In Touch - Last Section */}
      <div className={`${styles.relative} ${styles.sectionPy20} ${styles.textCenter}`}>
        <div className={styles.maxW4xl}>
          <h2 className={`${styles.sectionTitle} ${styles.mb8}`}>
            Want to see more?
          </h2>
          <p className={`${styles.textXl} ${styles.mb10}`}>
            Check out the full prototype and design system on Figma
          </p>
          <button className={styles.ctaButton} onClick={() => navigate('/contact')}>
            <Figma className="w-6 h-6" />
            Get In Touch
            <ArrowRight className={`w-6 h-6 ${styles.arrowIcon}`} />
          </button>
        </div>
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
}